import { h } from 'vue'
import { createDiscreteApi } from 'naive-ui'
import { PentaError } from '@/utils/PentaError'
import errorLogService from '@/services/errorLog.service'

// Color del toast genérico, calcado de .toastError (src/frontend/mobile/chat/chat.less) para mantener la estética histórica de PentaError
const TOAST_ERROR_COLOR = '#E53935'

const { notification, dialog, message } = createDiscreteApi(['notification', 'dialog', 'message'], {
  configProviderProps: {
    themeOverrides: {
      Message: {
        colorError: TOAST_ERROR_COLOR,
        textColorError: '#fff',
        iconColorError: '#fff',
        closeIconColorError: '#fff',
        closeIconColorHoverError: '#fff',
        closeIconColorPressedError: '#fff',
        borderRadius: '5px'
      }
    }
  },
  messageProviderProps: {
    placement: 'top-right'
  }
})

let errorVisible = false

// Puerto de src/frontend/mobile/utils/errorHandler.js: punto central para mostrar cualquier error al usuario en Vue.
// Se llama automáticamente desde el catcher global de main.ts (unhandledrejection / app.config.errorHandler),
// y puede llamarse manualmente desde el catch de un componente para mostrar el error en el momento.
// errorVisible solo evita superponer dos dialogs modales (showFrontendInfoDialog libera la guarda ella misma, en su onAfterLeave):
// notification/message no son modales, naive-ui ya las apila correctamente, así que ahí la guarda se libera antes del await de
// logFrontendError (frontendInfo ya está en pentaError en este punto, no depende del round-trip al log) y no después.
export async function handlePentaError(error: unknown): Promise<void> {
  // Tanto app.config.errorHandler como el preventDefault de unhandledrejection silencian la consola: se loguea acá para no perder el stack
  console.error(error)
  if (errorVisible) return
  errorVisible = true
  try {
    const pentaError = error instanceof PentaError ? error : new PentaError(toPentaErrorData(error))
    if (!pentaError.frontendInfo) errorVisible = false
    if (!pentaError._id) await logFrontendError(pentaError)
    showError(pentaError)
  } catch {
    errorVisible = false
  }
}

// Convierte cualquier valor lanzado/rechazado (string, Error, objeto plano) al shape que espera PentaError
function toPentaErrorData(error: unknown) {
  if (error instanceof Error) return { message: error.message, name: error.name, stack: error.stack, source: 'vue' }
  if (typeof error === 'string') return { message: error, name: 'string Error', source: 'vue' }
  return { message: 'generic', name: 'GENERIC', source: 'vue' }
}

// Persiste el error en /api/errorLog cuando no vino ya guardado por el backend, y completa _id/randId
async function logFrontendError(pentaError: PentaError): Promise<void> {
  try {
    const loggedError = await errorLogService.save(pentaError)
    pentaError._id = loggedError._id
    pentaError.randId = loggedError.randId
    pentaError.ts = loggedError.ts
  } catch {
    // Si falla el log, randId/_id quedan undefined: notification.error no depende de ellos, showGenericToast
    // usa un texto sin código (ver fallback en showGenericToast) para que el usuario siga viendo feedback
  }
}

// Decide la UI según el error: modal rico si trae frontendInfo, mensaje real si es un PentaError de negocio, toast discreto si es genérico
function showError(pentaError: PentaError): void {
  if (pentaError.frontendInfo) return showFrontendInfoDialog(pentaError)
  if (pentaError.name === 'PentaError') {
    notification.error({
      title: window.i18next.t('Atención'),
      content: pentaError.message,
      closable: true,
      duration: 6000
    })
    return
  }
  showGenericToast(pentaError)
}

// El dialog es modal: dos superpuestos se ven rotos, por eso mantiene la guarda de errorVisible hasta que el usuario lo cierra
function showFrontendInfoDialog(pentaError: PentaError): void {
  const config = pentaError.frontendInfo!
  dialog.warning({
    title: config.title || window.i18next.t('Atención'),
    content: config.message || pentaError.message,
    positiveText: config.actions?.[0]?.label || window.i18next.t('CERRAR'),
    onAfterLeave: () => { errorVisible = false }
  })
}

// Toast rojo chico, estética heredada de .toastError (ver TOAST_ERROR_COLOR) para el caso genérico sin mensaje real
function showGenericToast(pentaError: PentaError): void {
  const randId = pentaError.randId
  const content = randId
    ? `${window.i18next.t('Ocurrió un problema.')} (${randId})`
    : window.i18next.t('Ocurrió un problema.')
  message.error(() => h('span', { style: 'display:inline-flex;align-items:center;gap:8px;' }, [
    content,
    randId && h('i', {
      class: 'fa-regular fa-copy',
      style: 'cursor:pointer;',
      title: window.i18next.t('Copiar código de error'),
      'data-testid': 'penta-error-copiar-codigo-boton',
      onClick: (event: MouseEvent) => { event.stopPropagation(); copyErrorCode(randId) }
    })
  ].filter(Boolean)), { duration: 5000 })
}

// Copia el código de error (randId) al portapapeles, mismo patrón que GetIdsModal.vue/IntegrationsModal.vue
async function copyErrorCode(randId: string): Promise<void> {
  if (!navigator.clipboard || !window.isSecureContext) return
  try {
    await navigator.clipboard.writeText(randId)
  } catch {
    // Si falla el copiado no hay feedback adicional que mostrar
  }
}
